/**
 * Display selectors for the NEXT UI: small pure helpers that turn the
 * parsed/analysis shapes into render-ready strings + buckets. No new analysis;
 * everything is already computed by the engine.
 */
import type { EmailAnalysis } from "../email-analysis";
import type { ParsedEmail } from "../email-parser";

export interface Address {
  name?: string;
  address?: string;
  domainUnicode?: string;
  domainAscii?: string;
}

/** Human label for an address: "Name <addr@host>" / "addr@host" / "n/a". */
export function addressLabel(a?: Address): string {
  if (!a) return "n/a";
  const addr = a.address?.trim();
  const name = a.name?.trim();
  if (name && addr) return `${name} <${addr}>`;
  if (addr) return addr;
  if (name) return name;
  return a.domainUnicode || a.domainAscii || "n/a";
}

export function addressList(list?: Address[]): string {
  if (!list || list.length === 0) return "n/a";
  return list.map(addressLabel).join(", ");
}

/**
 * The claimed From address, or undefined when the message names nobody.
 * One accessor so call sites stop writing `identity.from[0] as Address`:
 * the engine's Address satisfies this structural shape already, and the cast
 * was only ever silencing `noUncheckedIndexedAccess`.
 */
export function fromAddress(analysis: EmailAnalysis): Address | undefined {
  return analysis.identity.from[0];
}

/**
 * The claimed sender as ONE label: display name, else domain, else address.
 * Empty when the message names nobody, so each surface supplies its own
 * placeholder (the verdict's localized "an unknown sender", a table's "-").
 *
 * Shared by the verdict headline and Compare's column heads so a message
 * can never be titled two different ways on two surfaces.
 */
export function senderLabel(analysis: EmailAnalysis): string {
  const from = fromAddress(analysis);
  const name = from?.name?.trim();
  if (name) return name;
  const domain = from?.domainUnicode?.trim() || from?.domainAscii?.trim();
  if (domain) return domain;
  return from?.address?.trim() || "";
}

/**
 * Does an Authentication-Results result word a HARD failure? One definition
 * for the chip tone and the verdict headline: they disagreed before, because
 * the verdict matched the whole result string against a fixed set while the
 * chip matched a word inside it. A result carrying a comment
 * ("fail (body hash did not verify)") coloured the chip red but was left out
 * of the headline's failed-method list.
 */
export function isHardFail(result: string): boolean {
  return /(^|\b)(fail|hardfail|permerror|policy|reject)\b/.test(
    result.trim().toLowerCase(),
  );
}

/** Bytes to "12.3 KB". */
export function formatBytes(n?: number): string {
  if (!n || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${i === 0 ? v : v.toFixed(1)} ${units[i]}`;
}

/** One Date in the locale-neutral exhibit form "YYYY-MM-DD HH:MM:SS UTC",
 *  or null when the Date is invalid (caller falls back to the raw string). */
export function fmtDateIso(d: Date): string | null {
  try {
    return d
      .toISOString()
      .replace("T", " ")
      .replace(/\.\d+Z$/, " UTC");
  } catch {
    return null;
  }
}

export function fmtDate(d?: { parsed: Date | null; raw: string } | null): string {
  if (!d) return "n/a";
  if (d.parsed) {
    const iso = fmtDateIso(d.parsed);
    if (iso) return iso;
  }
  return d.raw || "n/a";
}

/** Swap the selectors' neutral "n/a" for a localized placeholder at display
 *  time. Selectors stay locale-free (exports and library storage keep "n/a");
 *  KV call sites pass t("val.unknown") here so chrome reads localized. */
export function localizeNa(s: string, unknown: string): string {
  return s === "n/a" ? unknown : s;
}

export type AuthTone = "pass" | "fail" | "warn" | "reported" | "neutral";

/** Classify an Authentication-Results method result into a chip tone.
 * NOTE: these are RELAY-REPORTED claims we cannot re-verify (#24 trust law),
 * so a "pass" reads as informational "reported", never verified-green. */
export function authTone(result: string): AuthTone {
  const r = result.trim().toLowerCase();
  if (isHardFail(r)) return "fail";
  if (/(^|\b)(softfail|temperror|neutral|none|quarantine)\b/.test(r)) return "warn";
  if (/\bpass\b/.test(r)) return "reported";
  return "neutral";
}

export interface AuthChip {
  method: string;
  result: string;
  tone: AuthTone;
  /** Distinguishing context (e.g. the DKIM/ARC signing domain or ARC instance)
   * so two results for one method, a pass AND a fail, read as DIFFERENT
   * signatures rather than a contradiction. A message can carry several DKIM
   * signatures (author domain + a forwarder/list); each is verified on its own. */
  context?: string;
}

/** Flatten every Authentication-Results entry into method chips. Deduped by
 * method + result + context, so two DKIM signatures (one passing, one failing)
 * survive as distinct, labelled chips instead of collapsing or contradicting. */
export function authChips(analysis: EmailAnalysis): AuthChip[] {
  const seen = new Map<string, AuthChip>();
  for (const block of analysis.auth) {
    for (const e of block.entries) {
      const dom =
        e.domain ||
        e.properties["header.d"] ||
        e.properties["header.i"] ||
        e.properties["smtp.mailfrom"];
      const inst = e.properties["i"] || e.properties["instance"];
      const ctxParts: string[] = [];
      if (dom) ctxParts.push(dom);
      if (inst && /^arc/i.test(e.method)) ctxParts.push(`i=${inst}`);
      const context = ctxParts.join(" · ") || undefined;
      const key = `${e.method.toLowerCase()}=${e.result.toLowerCase()}=${context ?? ""}`;
      if (!seen.has(key)) {
        seen.set(key, {
          method: e.method.toUpperCase(),
          result: e.result,
          tone: authTone(e.result),
          context,
        });
      }
    }
  }
  return [...seen.values()];
}

/** True when any method (e.g. DKIM, ARC-DKIM) reports BOTH a pass and a fail:
 * the case that looks contradictory until you see it's two signatures. */
export function authHasSplit(chips: AuthChip[]): boolean {
  const byMethod = new Map<string, Set<string>>();
  for (const c of chips) {
    const m = c.method.toLowerCase();
    if (!byMethod.has(m)) byMethod.set(m, new Set());
    byMethod
      .get(m)!
      .add(
        c.tone === "fail"
          ? "fail"
          : c.tone === "reported" || /pass/i.test(c.result)
            ? "pass"
            : "other",
      );
  }
  for (const set of byMethod.values()) {
    if (set.has("fail") && set.has("pass")) return true;
  }
  return false;
}

/** Stable, language-independent stat id. Drives BOTH the display label (via
 *  t('stat.<id>')) and the jump-to-section target, so localizing the label can
 *  never break navigation. */
export type ReportStatId = "hops" | "links" | "attachments" | "defects";

export interface ReportStat {
  num: number;
  id: ReportStatId;
  alert?: boolean;
}

/** The headline counts for the verdict stat strip. */
export function reportStats(parsed: ParsedEmail, analysis: EmailAnalysis): ReportStat[] {
  const defects = analysis.defects.length + parsed.defects.length;
  const suspicious = analysis.linkStats.suspicious + analysis.linkStats.mismatched;
  return [
    { num: analysis.delivery.length, id: "hops" },
    { num: analysis.linkStats.total, id: "links", alert: suspicious > 0 },
    { num: parsed.attachments.length, id: "attachments" },
    { num: defects, id: "defects", alert: defects > 0 },
  ];
}
